import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const EMPTY_FORM = {
  ID: null,
  AD_SOYAD: "",
  PROFIL_FOTO: "",
  TELEFON: "",
  DURUM: "Aktif",
};

const Moderatorler = () => {
  document.title = "Moderatörler | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [liste, setListe] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchListe = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/MODERATORLER?$orderby=ID desc`, { headers });
      const result = await res.json();
      setListe(result.value || []);
    } catch {
      setError("Moderatörler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchListe(); }, []);

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setModal(true);
  };

  const handleEdit = (row) => {
    setFormData({
      ID: row.ID,
      AD_SOYAD: row.AD_SOYAD || "",
      PROFIL_FOTO: row.PROFIL_FOTO || "",
      TELEFON: row.TELEFON || "",
      DURUM: row.DURUM || "Aktif",
    });
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.AD_SOYAD.trim()) {
      setError("Ad Soyad zorunludur.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        AD_SOYAD: formData.AD_SOYAD,
        PROFIL_FOTO: formData.PROFIL_FOTO || null,
        TELEFON: formData.TELEFON || null,
        DURUM: formData.DURUM,
      };

      if (isEditing) {
        await fetch(`${BASE_URL}/MODERATORLER(${formData.ID})`, {
          method: "PATCH", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Moderatör güncellendi.");
      } else {
        await fetch(`${BASE_URL}/MODERATORLER`, {
          method: "POST", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Moderatör eklendi.");
      }

      setModal(false);
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Kayıt sırasında hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu moderatörü silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/MODERATORLER(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Moderatör silindi.");
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "#",
      accessorKey: "ID",
      enableSorting: true,
      cell: (c) => <strong>#{c.getValue()}</strong>,
    },
    {
      header: "Profil",
      accessorKey: "PROFIL_FOTO",
      enableSorting: false,
      cell: (c) => {
        const url = c.getValue();
        const name = c.row.original.AD_SOYAD;
        return url ? (
          <img
            src={url} alt={name}
            style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="avatar-xs">
            <span className="avatar-title rounded-circle bg-primary-subtle text-primary">
              {name?.charAt(0)}
            </span>
          </div>
        );
      },
    },
    {
      header: "Ad Soyad",
      accessorKey: "AD_SOYAD",
      enableSorting: true,
      cell: (c) => <span className="fw-medium">{c.getValue()}</span>,
    },
    {
      header: "Telefon",
      accessorKey: "TELEFON",
      enableSorting: false,
      cell: (c) => c.getValue() || <span className="text-muted">---</span>,
    },
    {
      header: "Durum",
      accessorKey: "DURUM",
      enableSorting: true,
      cell: (c) => {
        const d = c.getValue();
        const color = d === "Aktif" ? "success" : d === "Pasif" ? "secondary" : "warning";
        return <Badge color={color} pill>{d || "Aktif"}</Badge>;
      },
    },
    {
      header: "Oluşturma Tarihi",
      accessorKey: "OLUSTURMA_TARIHI",
      enableSorting: true,
      cell: (c) => c.getValue()
        ? new Date(c.getValue()).toLocaleDateString("tr-TR")
        : <span className="text-muted">---</span>,
    },
    {
      header: "İşlemler",
      enableSorting: false,
      cell: (cellProps) => {
        const row = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => handleEdit(row)}>
              <i className="bx bx-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(row.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  const f = (field, val) => setFormData(p => ({ ...p, [field]: val }));

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Yönetim" breadcrumbItem="Moderatörler" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">Moderatör Listesi</h4>
                    <Button color="primary" size="sm" onClick={openAdd}>
                      <i className="bx bx-plus me-1" />Yeni Moderatör
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={liste}
                      isGlobalFilter={true}
                      isPagination={true}
                      customPageSize={15}
                      SearchPlaceholder="Moderatör ara..."
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered>
        <ModalHeader toggle={() => setModal(false)}>
          <i className={`mdi ${isEditing ? "mdi-account-edit" : "mdi-account-plus"} me-2 text-primary`}></i>
          {isEditing ? `Moderatörü Düzenle (#${formData.ID})` : "Yeni Moderatör Ekle"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className="fw-bold">Ad Soyad <span className="text-danger">*</span></Label>
            <Input
              value={formData.AD_SOYAD}
              onChange={(e) => f("AD_SOYAD", e.target.value)}
              placeholder="Ad ve soyad giriniz"
            />
          </FormGroup>
          <FormGroup>
            <Label className="fw-bold">Telefon</Label>
            <Input
              value={formData.TELEFON}
              onChange={(e) => f("TELEFON", e.target.value)}
              placeholder="05xx xxx xx xx"
            />
          </FormGroup>
          <FormGroup>
            <Label className="fw-bold">Durum</Label>
            <Input type="select" value={formData.DURUM} onChange={(e) => f("DURUM", e.target.value)}>
              <option value="Aktif">Aktif</option>
              <option value="Pasif">Pasif</option>
              <option value="İzinli">İzinli</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label className="fw-bold">Profil Fotoğrafı URL</Label>
            <Input
              value={formData.PROFIL_FOTO}
              onChange={(e) => f("PROFIL_FOTO", e.target.value)}
              placeholder="https://..."
            />
          </FormGroup>
          {formData.PROFIL_FOTO && (
            <div className="text-center mt-2">
              <img
                src={formData.PROFIL_FOTO} alt="Önizleme"
                style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", border: "2px solid #dee2e6" }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)} disabled={isSaving}>İptal</Button>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-content-save me-1"></i>}
            {isEditing ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Moderatorler;
